import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import {
  clearPendingReferralToken,
  decodeReferralToken,
  savePendingReferralToken,
} from "@/lib/referrals";

/**
 * Amber invite banner rendered on a public course page when the URL carries
 * `?ref=<token>`. Persists the token in localStorage so it survives login /
 * signup redirects and gets forwarded to self-enroll.
 */
export default function ReferralBanner({ courseId, token }) {
  const [decoded, setDecoded] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token) return;
    decodeReferralToken(token)
      .then((r) => {
        if (r?.valid && r.course_id === courseId) {
          savePendingReferralToken(courseId, token);
          setDecoded(r);
        } else {
          clearPendingReferralToken();
          setDecoded(null);
        }
      })
      .catch(() => setDecoded(null));
  }, [token, courseId]);

  if (!decoded || dismissed) return null;

  const referrer = decoded.referrer_name || "A friend";
  const bonus = Math.round(decoded.referred_bonus_inr || 50);

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
      data-testid="referral-banner"
    >
      <Sparkles className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <div className="font-medium">
          🎉 You&apos;ve been invited by{" "}
          <span data-testid="referral-banner-referrer">{referrer}</span>!
        </div>
        <div className="text-xs mt-0.5 text-amber-800">
          Enroll and complete payment — you&apos;ll both earn{" "}
          <span className="font-semibold" data-testid="referral-banner-bonus">
            ₹{bonus}
          </span>{" "}
          in credit toward future purchases.
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-700 hover:text-amber-900"
        aria-label="Dismiss invite"
        data-testid="referral-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
